const WALLPAPER_SPECS = Object.freeze({
  rollWidth: 50,
  rollLength: 1000,
  verticalRepeat: 50,
  cuttingAllowance: 10,
});

function calculateWallpaper(wallWidth, wallHeight) {
  const specs = WALLPAPER_SPECS;
  const strings = window.wallpaperStrings || {};
  const errors = strings.errors || {};

  if (
    !Number.isFinite(wallWidth) ||
    !Number.isFinite(wallHeight) ||
    wallWidth <= 0 ||
    wallHeight <= 0
  ) {
    throw new Error(
      errors.invalidDimensions || "Please enter a valid wall width and height."
    );
  }

  const width = Math.ceil(wallWidth);
  const height = Math.ceil(wallHeight);

  const panelsNeeded = Math.ceil(width / specs.rollWidth);
  const requiredPanelLength = height + specs.cuttingAllowance;
  const adjustedPanelLength =
    Math.ceil(requiredPanelLength / specs.verticalRepeat) * specs.verticalRepeat;

  if (adjustedPanelLength > specs.rollLength) {
    throw new Error(
      errors.heightTooLarge ||
        "The entered wall height is too large for this wallpaper roll."
    );
  }

  const panelsPerRoll = Math.floor(specs.rollLength / adjustedPanelLength);

  if (panelsPerRoll < 1) {
    throw new Error(
      errors.heightTooLarge ||
        "The entered wall height is too large for this wallpaper roll."
    );
  }

  const rollsNeeded = Math.ceil(panelsNeeded / panelsPerRoll);
  const totalPanelsSupplied = rollsNeeded * panelsPerRoll;

  return {
    rollsNeeded,
    panelsNeeded,
    requiredPanelLength,
    adjustedPanelLength,
    panelsPerRoll,
    totalPanelsSupplied,
    sparePanels: totalPanelsSupplied - panelsNeeded,
    coveredWidth: panelsNeeded * specs.rollWidth,
    unusedLengthPerRoll: specs.rollLength - panelsPerRoll * adjustedPanelLength,
  };
}

class WallpaperCalculator extends HTMLElement {
  constructor() {
    super();

    this.strings = window.wallpaperStrings || {};
    this.result = null;
    this.adding = false;
    this.onProductVariantChange = this.handleProductVariantChange.bind(this);
  }

  connectedCallback() {
    const productData = document.getElementById("master-product-data");
    this.product = productData ? JSON.parse(productData.textContent)[0] : null;
    const firstInput = this.optionInputs[0];
    const firstVariant = this.product?.variants.find(
      (variant) => String(variant.id) === firstInput?.dataset.variantId
    );
    this.backingOptionIndex = Math.max(0, firstVariant?.options.indexOf(firstInput.value) ?? 0);
    this.hasDesignOptions = this.product?.options_with_values.some(
      (option, index) => index !== this.backingOptionIndex && option.values.length > 1
    );
    this.syncDesignOptions(this.selectedOption);
    document.addEventListener("product:variant-change", this.onProductVariantChange);

    this.bindEvents();
    this.recalculate();
  }

  disconnectedCallback() {
    document.removeEventListener("product:variant-change", this.onProductVariantChange);
  }

  syncDesignOptions(input) {
    if (!this.hasDesignOptions || !input) return;
    const selected = this.product.variants.find(
      (variant) => String(variant.id) === input.dataset.variantId
    );
    if (!selected) return;
    const matchesDesign = (variant, reference) => variant.options.every(
      (value, index) => index === this.backingOptionIndex || value === reference.options[index]
    );
    this.optionInputs.forEach((radio) => {
      const variant = this.product.variants.find(
        (item) => String(item.id) === radio.dataset.variantId
      );
      if (!variant) return;
      const row = radio.closest(".wpc__option");
      if (row) row.hidden = !matchesDesign(variant, selected);
      radio.dataset.title = variant.title;
      const imageVariant = variant.featured_image ? variant : this.product.variants.find(
        (item) => item.featured_image && matchesDesign(item, variant)
      );
      if (imageVariant?.featured_image?.src) radio.dataset.image = imageVariant.featured_image.src;
    });
  }

  handleProductVariantChange(event) {
    if (!this.hasDesignOptions) return;
    const input = Array.from(this.optionInputs).find(
      (radio) => radio.dataset.variantId === String(event.detail?.variant?.id)
    );
    if (!input) return;
    input.checked = true;
    this.handleOptionChange(input, false);
  }

  get widthInput() {
    return this.querySelector("[data-wpc-width]");
  }

  get heightInput() {
    return this.querySelector("[data-wpc-height]");
  }

  get optionInputs() {
    return this.querySelectorAll("[data-wpc-option]");
  }

  get errorEl() {
    return this.querySelector("[data-wpc-error]");
  }

  get popup() {
    const popup = document.getElementById("wallpaperCalculatorPopup");
    if (popup && !popup.dataset.wpcBound) initPopup(popup);

    return popup;
  }

  bindEvents() {
    this.addEventListener("input", (e) => {
      if (!e.target.closest("[data-wpc-width], [data-wpc-height]")) return;

      this.recalculate();
      this.syncPopupInputs();
    });

    this.addEventListener("change", (e) => {
      const radio = e.target.closest("[data-wpc-option]");
      if (radio) this.handleOptionChange(radio);
    });
  }

  get popupWidthInput() {
    return this.popup?.querySelector("[data-wpc-popup-width]");
  }

  get popupHeightInput() {
    return this.popup?.querySelector("[data-wpc-popup-height]");
  }

  get popupOptionTitle() {
    return this.popup?.querySelector("[data-wpc-popup-option]");
  }

  get popupRolls() {
    return this.popup?.querySelector("[data-wpc-popup-rolls]");
  }

  get popupImages() {
    return this.popup?.querySelectorAll("[data-wpc-popup-image]") || [];
  }

  get popupPrice() {
    return this.popup?.querySelector("[data-wpc-popup-price]");
  }

  get popupComment() {
    return this.popup?.querySelector("[data-wpc-popup-comment]");
  }

  get popupError() {
    return this.popup?.querySelector("[data-wpc-popup-error]");
  }

  get popupAtcBtn() {
    return this.popup?.querySelector("[data-wpc-popup-atc]");
  }

  get selectedOption() {
    const options = Array.from(this.optionInputs).filter(
      (input) => !input.closest(".wpc__option")?.hidden
    );

    return (
      options.find((input) => input.checked && !input.disabled) ||
      options.find((input) => !input.disabled) ||
      null
    );
  }

  getDimensions() {
    const parse = (input) =>
      Number.parseFloat(String(input?.value || "").replace(",", "."));

    return {
      width: parse(this.widthInput),
      height: parse(this.heightInput),
    };
  }

  recalculate() {
    const { width, height } = this.getDimensions();

    try {
      this.result = calculateWallpaper(width, height);
      this.showError(null);
      this.clearFieldErrors();
    } catch (error) {
      this.result = null;
      const touched =
        String(this.widthInput?.value || "") !== "" &&
        String(this.heightInput?.value || "") !== "";
      this.showError(touched ? error.message : null);
    }

    this.renderResult();
    this.updateProductPrice();
  }

  renderResult() {
    this.renderPopupPrice();
    if (!this.popupRolls) return;

    if (!this.result) {
      this.popupRolls.textContent = "";
      return;
    }

    const rolls = this.result.rollsNeeded;
    const label =
      rolls === 1
        ? this.strings.rollSingular || "roll"
        : this.strings.rollPlural || "rolls";

    this.popupRolls.textContent = `${rolls} ${label}`;
  }

  showError(message) {
    [this.errorEl, this.popupError].forEach((el) => {
      if (!el) return;

      el.textContent = message || "";
      el.classList.toggle("hidden", !message);
    });
  }

  markInvalidFields() {
    const { width, height } = this.getDimensions();

    [
      [this.widthInput, width],
      [this.heightInput, height],
    ].forEach(([input, value]) => {
      if (!input) return;

      const invalid = !Number.isFinite(value) || value <= 0;
      input
        .closest("[data-wpc-field]")
        ?.classList.toggle("wpc__field--error", invalid);
    });
  }

  clearFieldErrors() {
    this.querySelectorAll("[data-wpc-field]").forEach((field) => {
      field.classList.remove("wpc__field--error");
    });
  }

  handleOptionChange(input, syncProduct = true) {
    if (input.dataset.variantId) {
      this.dataset.variantId = input.dataset.variantId;
    }

    this.syncDesignOptions(input);
    this.updateProductPrice();
    this.updateVariantInUrl(input);
    this.syncPopupOption();
    if (syncProduct && this.hasDesignOptions) {
      document.dispatchEvent(new CustomEvent("wallpaper:variant-change", {
        detail: { variantId: input.dataset.variantId },
      }));
    }
  }

  syncPopupOption() {
    const option = this.selectedOption;
    if (!option) return;

    if (this.popupOptionTitle) {
      this.popupOptionTitle.textContent = option.dataset.title || "";
    }
    if (option.dataset.image) {
      this.popupImages.forEach((img) => {
        img.src = option.dataset.image;
      });
    }
    this.renderPopupPrice();
  }

  // Price of the rolls needed for the entered wall (one roll until calculated)
  get totalPrice() {
    const option = this.selectedOption;
    const unitPrice = Number(option?.dataset.price ?? this.dataset.price);
    if (!Number.isFinite(unitPrice)) return null;

    return unitPrice * (this.result?.rollsNeeded || 1);
  }

  renderPopupPrice() {
    const priceEl = this.popupPrice;
    const total = this.totalPrice;
    if (!priceEl || total === null) return;

    const money = this.formatMoney(total);
    if (money) priceEl.textContent = money;
  }

  formatMoney(amount) {
    if (!window.Shopify?.formatMoney || !window.themeData?.moneyFormat) {
      return null;
    }

    return Shopify.formatMoney(amount, window.themeData.moneyFormat);
  }

  updateProductPrice() {
    const total = this.totalPrice;
    if (total === null) return;
    document.dispatchEvent(new CustomEvent("wallpaper:price-change", {
      detail: { productId: this.product?.id, price: total },
    }));
  }

  updateVariantInUrl(input) {
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({ path: url.href }, "", url.href);
  }

  openCalculatorPopup() {
    this.recalculate();

    if (!this.result) {
      this.markInvalidFields();
      this.showError(
        this.errorEl?.textContent ||
          this.strings.errors?.invalidDimensions ||
          "Please enter a valid wall width and height."
      );
      this.widthInput?.focus();
      return;
    }

    const popup = this.popup;
    if (!popup) return;

    this.syncPopupInputs();
    this.syncPopupOption();
    this.renderResult();

    popup.setAttribute("open", "");
    document.body.classList.add("overflow-hidden");
  }

  closeCalculatorPopup() {
    this.popup?.removeAttribute("open");
    document.body.classList.remove("overflow-hidden");
  }

  syncPopupInputs() {
    if (this.popupWidthInput) {
      this.popupWidthInput.value = this.widthInput?.value || "";
    }
    if (this.popupHeightInput) {
      this.popupHeightInput.value = this.heightInput?.value || "";
    }
  }

  buildCartItem() {
    const option = this.selectedOption;
    const variantId = Number(
      option?.dataset.variantId || this.dataset.variantId
    );
    const { width, height } = this.getDimensions();

    if (!Number.isInteger(variantId) || variantId <= 0) {
      console.error("[wallpaper-calculator] unusable variant id", {
        fromOption: option?.dataset.variantId,
        fromElement: this.dataset.variantId,
        optionCount: this.optionInputs.length,
        connected: this.isConnected,
      });
      throw new Error(
        this.strings.errors?.invalidVariant || "Cannot find variant"
      );
    }

    const properties = {
      Width: Math.ceil(width) + " cm",
      Height: Math.ceil(height) + " cm",
      Rolls: String(this.result.rollsNeeded),
      _product_url: window.location.href,
      // Unique per add so Shopify never merges two calculator lines into one
      _wpc_id: Date.now().toString(36),
    };

    if (option?.dataset.title) {
      properties.Option = option.dataset.title;
    }

    const image = option?.dataset.image || this.dataset.productImage;
    if (image) properties._product_image = image;

    const comment = String(this.popupComment?.value || "").trim();
    if (comment) properties.Comment = comment;

    return { id: variantId, quantity: this.result.rollsNeeded, properties };
  }

  async addToCart() {
    const atcBtn = this.popupAtcBtn;
    if (this.adding || !this.result || !atcBtn) return;

    this.adding = true;
    atcBtn.disabled = true;
    atcBtn.classList.add("adding-to-cart");

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: [this.buildCartItem()] }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || "Add to cart failed");
      }

      this.closeCalculatorPopup();
      if (this.popupComment) this.popupComment.value = "";

      document.querySelector("sc-cart-drawer")?.setAttribute("adding", "");
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.adding = false;
      atcBtn.disabled = false;
      atcBtn.classList.remove("adding-to-cart");
    }
  }
}

function currentCalculator() {
  return document.querySelector("wallpaper-calculator");
}

function initPopup(popup) {
  popup.dataset.wpcBound = "true";

  popup.querySelectorAll("[data-wpc-popup-close]").forEach((closeBtn) => {
    closeBtn.addEventListener("click", () =>
      currentCalculator()?.closeCalculatorPopup()
    );
  });

  popup
    .querySelectorAll("[data-wpc-popup-width], [data-wpc-popup-height]")
    .forEach((input) => {
      input.addEventListener("input", () => {
        const calculator = currentCalculator();
        if (!calculator) return;

        const target = input.hasAttribute("data-wpc-popup-width")
          ? calculator.widthInput
          : calculator.heightInput;

        if (target) target.value = input.value;
        calculator.recalculate();
      });
    });

  popup
    .querySelector("[data-wpc-popup-atc]")
    ?.addEventListener("click", () => currentCalculator()?.addToCart());

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && popup.hasAttribute("open")) {
      currentCalculator()?.closeCalculatorPopup();
    }
  });

  document.addEventListener("click", (e) => {
    if (e.target === document.body && popup.hasAttribute("open")) {
      currentCalculator()?.closeCalculatorPopup();
    }
  });
}

document.addEventListener("wallpaper:add-to-cart-click", () =>
  currentCalculator()?.openCalculatorPopup()
);

customElements.define("wallpaper-calculator", WallpaperCalculator);
